/**
 * Mobile entrypoint for the api shim.
 *
 * The shim itself lives in shared/api-shim.ts so both bundles can reach it —
 * app/src can only import from app/src and shared, never from mobile/. What
 * stays here is the part that is genuinely mobile-only: binding the shim to
 * the relay singleton, and installing it as the PROCESS-WIDE `window.api`.
 *
 * `installApiShim` must remain the only thing that sets `__CODEFLOWS_REMOTE`
 * and clears `window.electron`. Those are process globals: a shim built for a
 * single shared tab must never touch them, or the whole desktop renderer would
 * start believing it is a remote client.
 */

import { createApiShim as createShim, type ApiShimTransport } from '@shared/api-shim'
import { relay } from './lib/relay'

export type { ApiShimTransport }

/**
 * Build the `window.api` stand-in over the mobile relay singleton.
 * Exported for tests; pass a transport to build one over something else.
 */
export function createApiShim(transport: ApiShimTransport = relay): Record<string, any> {
  return createShim(transport)
}

/** Install the shim. Must run before any component reads `window.api`. */
export function installApiShim(): void {
  ;(window as any).api = createApiShim(relay)

  // "This renderer is driving a desktop over the relay", NOT "this is a small
  // screen" — layout is a media query now (app/src/lib/environment.ts). Only
  // genuinely platform-shaped decisions read this: window chrome, the
  // synchronous quit flush, and how many session tabs stay mounted.
  ;(window as any).__CODEFLOWS_REMOTE = true

  // Marks this as not-Electron for anything sniffing the other bridge.
  ;(window as any).electron = undefined
}
